import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/common/Button';
import './ForgotPassword.css';

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:5000/api'
    : '/api')
).replace(/\/+$/, '');

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const requestInProgress = useRef(false);

  const sendResetEmail = async (emailAddress) => {
    if (requestInProgress.current) {
      return;
    }

    const normalizedEmail = emailAddress.trim().toLowerCase();

    setError('');
    setNotice('');

    if (
      !normalizedEmail ||
      normalizedEmail.length > 254 ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
    ) {
      setError('Please enter a valid email address.');
      return;
    }

    requestInProgress.current = true;
    setLoading(true);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ email: normalizedEmail }),
        signal: controller.signal
      });

      const data = await response.json().catch(() => null);

      if (response.status === 404 || response.status === 405) {
        throw new Error(
          'The password reset service is unavailable. Please contact support.'
        );
      }

      if (response.status === 429) {
        throw new Error(
          'Too many reset requests. Please wait a few minutes and try again.'
        );
      }

      if (!response.ok || data?.success === false) {
        const serverMessage =
          typeof data?.message === 'string'
            ? data.message
            : typeof data?.error === 'string'
              ? data.error
              : '';

        throw new Error(
          response.status >= 500
            ? 'The server could not process your reset request. Please try again later.'
            : serverMessage || 'Unable to request a reset link. Please try again.'
        );
      }

      if (!data || typeof data !== 'object') {
        throw new Error(
          'The server returned an unexpected response. Please contact support.'
        );
      }

      setEmail(normalizedEmail);
      setSubmittedEmail(normalizedEmail);
      setSubmitted(true);
      setNotice(
        'If this email belongs to an eligible account, you will receive a password reset link. Please check your inbox and spam folder.'
      );
    } catch (err) {
      if (err.name === 'AbortError') {
        setError(
          'The request timed out. Check your inbox before requesting another link.'
        );
      } else if (err instanceof TypeError) {
        setError(
          'Unable to connect to the server. Please check your connection and try again.'
        );
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      clearTimeout(timeoutId);
      requestInProgress.current = false;
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendResetEmail(email);
  };

  const handleResend = () => {
    sendResetEmail(submittedEmail);
  };

  const handleChangeEmail = () => {
    if (requestInProgress.current) {
      return;
    }

    setSubmitted(false);
    setError('');
    setNotice('');
  };

  return (
    <div className="forgot-password-page">
      <div className="forgot-container" aria-busy={loading}>
        {submitted ? (
          <>
            <div className="success-icon">
              <i className="fas fa-envelope" aria-hidden="true"></i>
            </div>

            <h1>Check your email</h1>

            <p className="subtitle">
              Password reset requested for <strong>{submittedEmail}</strong>
            </p>

            {notice && (
              <p className="text-muted" role="status" aria-live="polite">
                {notice}
              </p>
            )}

            {error && (
              <div className="error-alert" role="alert">
                <i
                  className="fas fa-exclamation-circle"
                  aria-hidden="true"
                ></i>
                <span>{error}</span>
              </div>
            )}

            <p className="text-muted">
              Didn't receive an email?{' '}
              <button
                type="button"
                className="resend-link"
                onClick={handleResend}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Resend reset link'}
              </button>
            </p>

            <p className="text-muted">
              <button
                type="button"
                className="resend-link"
                onClick={handleChangeEmail}
                disabled={loading}
              >
                Use a different email address
              </button>
            </p>

            <Link to="/login" className="back-link">
              <i className="fas fa-arrow-left" aria-hidden="true"></i>{' '}
              Back to Login
            </Link>
          </>
        ) : (
          <>
            <div className="logo">
              <i className="fas fa-anchor" aria-hidden="true"></i>
              <span>SeaZex</span>
            </div>

            <h1>Forgot password?</h1>

            <p className="subtitle">
              Enter your registered email address to request a password reset
              link.
            </p>

            {error && (
              <div className="error-alert" id="forgot-error" role="alert">
                <i
                  className="fas fa-exclamation-circle"
                  aria-hidden="true"
                ></i>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="forgot-email">Email Address</label>
                <input
                  id="forgot-email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setError('');
                  }}
                  placeholder="Enter your registered email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  maxLength={254}
                  disabled={loading}
                  aria-describedby={error ? 'forgot-error' : undefined}
                  required
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={loading || !email.trim()}
              >
                {loading && (
                  <i
                    className="fas fa-spinner fa-spin"
                    aria-hidden="true"
                  ></i>
                )}
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>

            <div className="footer-links">
              <Link to="/login">
                <i className="fas fa-arrow-left" aria-hidden="true"></i>{' '}
                Back to Login
              </Link>
              <Link to="/register">Create an account</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;